import { useEffect, useState } from 'react';

import { translate } from '../../../common/helpers/translate.helper';
import { Spinner } from '../../../common/Spinner/Spinner';
import { Box } from '../../../common/ui/Box';
import { Button } from '../../Button/Button';
import { ComboBox, ComboBoxOption } from '../../ComboBox/ComboBox';
import { Text } from '../../Text/Text';
import { FilterPill, FilterPillProps } from '../FilterPill/FilterPill';

interface FilterSelectFullProps {
    label: FilterPillProps['label'];
    filterByLabel: string;
    filterPlaceholder?: string;
    options: ComboBoxOption[];
    value?: string;
    onChange?: (value?: string) => void;
    sortOptions?: (a: ComboBoxOption, b: ComboBoxOption) => number;
    isLoading?: boolean;
    isError?: boolean;
}

export function FilterSelectFull({
    label,
    filterByLabel,
    filterPlaceholder,
    value,
    options,
    onChange,
    sortOptions,
    isLoading,
    isError,
}: FilterSelectFullProps) {
    const [visibleOption, setVisibleOption] = useState<string | null>(null);
    const selectedOption = options.find((option) => option.value === value) ?? null;

    useEffect(() => {
        setVisibleOption(selectedOption?.value ?? null);
    }, [selectedOption]);

    const sortedOptions = [...options].sort(sortOptions || ((a, b) => a.label.localeCompare(b.label)));

    return (
        <FilterPill
            label={label}
            value={options.find((option) => option.value === visibleOption)?.label}
            onClear={() => {
                setVisibleOption(null);
                onChange?.(undefined);
            }}
            onPopoverClose={() => {
                setVisibleOption(selectedOption?.value ?? null);
            }}
        >
            {(closePopover) => {
                if (isLoading) {
                    return (
                        <Box padding="12px" display="flex" justifyContent="center">
                            <Spinner />
                        </Box>
                    );
                }

                return (
                    <Box padding="12px" display="flex" flexDirection="column" gap={16}>
                        <Box display="flex" alignItems="center" justifyContent="space-between" gap={16}>
                            <Text variant="body" fontWeight={600}>
                                {filterByLabel}
                            </Text>
                            <Button
                                type="submit"
                                size="small"
                                emphasis="high"
                                onClick={() => {
                                    onChange?.(visibleOption ?? undefined);
                                    closePopover();
                                }}
                            >
                                {translate('global.filterMultiSelect.apply')}
                            </Button>
                        </Box>
                        <Box display="flex" flexDirection="column" gap={8}>
                            <ComboBox
                                negative={isError}
                                caption={isError ? translate('global.genericError') : undefined}
                                searchPlaceholder={filterPlaceholder}
                                onChange={setVisibleOption}
                                value={visibleOption}
                                options={sortedOptions}
                                isClearable
                            />
                        </Box>
                    </Box>
                );
            }}
        </FilterPill>
    );
}
